import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const PlanOptionMenuCard = ({
  handleSelectButtonClick,
  isSelected,
  goods,
  selectedOutboundDate,
  itineraryIndex,
}) => {
  const [imageSizeClass, setImageSizeClass] = useState('');
  const [imageRatioClass, setImageRatioClass] = useState('');
  const imageRef = useRef(null);

  // 画像サイズに基づいてCSSクラスを適用
  const applyImageSizeClass = (img) => {
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    
    // 幅280px未満または高さ187px未満の場合は小さい画像として扱う
    if (naturalWidth < 280 || naturalHeight < 187) {
      setImageSizeClass('small-image');
    } else {
      setImageSizeClass('large-image');
    }
  };

  // 画像に比率に応じたCSSクラスを適用
  const applyImageRatioClass = (img) => {
    const ratio = img.naturalWidth / img.naturalHeight;
    
    // 閾値1.4で判定（3:2=1.5に近い画像は横長として扱う）
    if (ratio >= 1.4) {
        // img.classList.add('img-landscape');
        setImageRatioClass('img-landscape');
    } else {
        // img.classList.add('img-portrait');
        setImageRatioClass('img-portrait');
    }
  };

  // 画像読み込み時の処理
  const handleImageLoad = (img) => {
    if (img.complete) {
      // 既に読み込まれている場合
      applyImageSizeClass(img);
      applyImageRatioClass(img);
    } else {
      // 読み込み完了を待つ
      img.addEventListener('load', function() {
        applyImageSizeClass(this);
        applyImageRatioClass(this);
      });
      
      // エラー時はデフォルト（大きい画像）として扱う
      img.addEventListener('error', function() {
        setImageSizeClass('large-image');
      });
    }
  };

  // 画像が存在する場合の処理
  useEffect(() => {
    if (goods.images && goods.images.length > 0 && goods.images[0].seq === '1' && imageRef.current) {
      handleImageLoad(imageRef.current);
    }
  }, [goods.images]);

  // もっと見るの表示制御
  const descriptionRef = useRef(null);
  const moreLinkRef = useRef(null);
  const [descriptionClass, setDescriptionClass] = useState("");
  const [showMoreLink, setShowMoreLink] = useState(false);

  if(!goods.calendarList || goods.calendarList.length <= 0) return null;

  const hasStockDateData = goods.calendarList.find((calendar) => calendar.stocks > 0);
  const status = hasStockDateData ? '○' : '×';

  useEffect(() => {
    // 一時的にクローンして全文の高さを測定
    if(!descriptionRef.current) return;
    const clone = descriptionRef.current.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.height = 'auto';
    clone.style.webkitLineClamp = 'unset';
    clone.style.display = 'block';
    clone.style.webkitBoxOrient = 'unset';
    clone.style.overflow = 'visible';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    clone.style.width = descriptionRef.current.offsetWidth + 'px'; // 幅を元の要素と同じに設定
    descriptionRef.current.parentNode.appendChild(clone);
    const originalHeight = descriptionRef.current.clientHeight;
    const fullHeight = clone.offsetHeight;
    descriptionRef.current.parentNode.removeChild(clone);

    if (fullHeight > originalHeight) {
        // テキストが長い場合、ボタンを表示し、初期状態を制御
        // ただし、在庫がない場合は非表示にする
        setShowMoreLink(hasStockDateData ? true : false);

        // is-expandedクラスがなければ省略表示
        if (!descriptionRef.current.classList.contains('is-expanded')) {
            setDescriptionClass('text-truncate');
        } else {
            setDescriptionClass('');
        }
    } else {
        // テキストが短い場合、ボタンを非表示
        setShowMoreLink(false);
        setDescriptionClass('');
    }
    if(goods.goodsBasicInfo.planExplanationDetail === '') {
      // 在庫がない場合は非表示にする
      setShowMoreLink(hasStockDateData ? true : false);
    }
    
    // 在庫がない場合は常に非表示にする
    if(!hasStockDateData) {
      setShowMoreLink(false);
    }
  }, [goods.goodsBasicInfo.planExplanationDetail, hasStockDateData]);
  const ADChargesData = hasStockDateData ? hasStockDateData : goods.calendarList[0];
  const ADcharges = ADChargesData.chargesList.find((charge) => charge.kind === 'AD');
  const ADPriceDiffFugou = ADcharges ? ADcharges.priceDiff.startsWith("-") ? Number(ADcharges.priceDiff).toLocaleString() : `+${Number(ADcharges.priceDiff).toLocaleString()}` : "";
  const ADPriceDiff = !hasStockDateData ? "-" : ADPriceDiffFugou;

  return (
    <div className={`pagePlanOptionMenuCard ${isSelected ? 'selected' : ''}`}>
      <div className="pagePlanOptionMenuContent">
        
        <div className={`pagePlanOptionMenuImage`}>
          <img 
            ref={imageRef}
            src={(goods.images.length > 0 && goods.images[0].seq === '1') ? goods.images[0].url : '/dp-air/img/noimage.png'} 
            className={`${imageSizeClass} ${imageRatioClass}`}
            alt="" 
            onLoad={(e) => handleImageLoad(e.target)}
            onError={() => setImageSizeClass('large-image')}
          />
        </div>
        <div className="pagePlanOptionMenuDetails">
          <div className="pagePlanOptionMenuInfo">
            <h3 className="pagePlanOptionMenuInfoTitle">
              ＜{goods.goodsDetailInfo.categoryDetail}＞{goods.landFacilityName} {goods.goodsName} {goods.goodsPlanName}／ {goods.area2Name} {goods.area3Name}
            </h3>
          </div>
          {(!!hasStockDateData || goods.goodsBasicInfo.planExplanationDetail !== '') && (
            <div className="pagePlanOptionMenuDescription">
              <div 
                ref={descriptionRef}
                className={`pagePlanOptionMenuDescriptionText ${descriptionClass}`}
                style={{
                  WebkitBoxOrient: 'vertical',
                }}
              >
                <p dangerouslySetInnerHTML={{ __html: goods.goodsBasicInfo.planExplanationDetail }}></p>
              </div>
              <a href="#" 
                ref={moreLinkRef}
                style={{display: showMoreLink ? 'inline-block' : 'none'}}
                className="toggle-text" 
                data-micromodal-trigger="selectModal" 
                onClick={() => handleSelectButtonClick(goods, itineraryIndex)}
              >
                <span className="toggle-text-label">もっと見る</span>
                <img src="/dp-air/img/icons/morelink.svg" alt="もっと見る" />
              </a>
            </div>
          )}
        </div>

        <div className="pagePlanOptionMenuStatus">
          <div className="pagePlanOptionMenuStatusLabels">
            <span className="pagePlanOptionMenuStatusLabel">空き状況：{status}</span>
            <span className="pagePlanOptionMenuStatusLabel">{goods.goodsDetailInfo.useUnit === "group" ? "合計差額" : "おとな1名様あたり差額"}：{ADPriceDiff}円</span>
          </div>
          <div className="pagePlanOptionMenuActions">
            {isSelected && (
              <div className="selected-label">
                <span>選択中</span>
              </div>
            )}
            <button 
              className={`button primary select-btn ${hasStockDateData ? '' : 'disabled'}`} 
              onClick={() => handleSelectButtonClick(goods, itineraryIndex)}
              disabled={!hasStockDateData}
            >
              {hasStockDateData ? '選択する' : '在庫なし'}
            </button>
          </div>
        </div>

      </div>
  </div>
  );
};

PlanOptionMenuCard.defaultProps = {
  handleSelectButtonClick: () => {},
  isSelected: false,
  goods: {},
  selectedOutboundDate: '',
  itineraryIndex: 0,
};

PlanOptionMenuCard.propTypes = {
  handleSelectButtonClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  goods: PropTypes.object.isRequired,
  selectedOutboundDate: PropTypes.string.isRequired,
  itineraryIndex: PropTypes.number.isRequired,
};

export default PlanOptionMenuCard;